import {
  ReactElement, useEffect, useState,
} from 'react';
import { FormModalContent } from './FormModalContent';
import {
  ModalOverlay, ModalContainer, CloseButton, ModalHeader,
  ModalContent,
} from './styles';
import { useModalControl } from 'hooks/useModalControl';
import { CloseModalX } from 'icons';
import { useTranslation } from 'react-i18next';
import { getUnitList } from '../../helpers';
import { getUserProfile } from 'helpers/userProfile';
import { handleUnitStatusByProfile } from 'helpers/unitStatus';
import { GetGoalsResponse, Option, SetGoalsResponse } from '../../types';
import { DefaultFormValues } from '../Form/types';
import { defaultFormValues } from '../Form/constants';
import { prepareAllUnitDataToForm } from 'helpers/energyGoals';

interface FormModalProps {
  clientId: number;
  mode: 'register' | 'edit';
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (responses: SetGoalsResponse[]) => void;
  data?: GetGoalsResponse;
  selectedInfo?: {
    unitId: number;
    year: number;
  };
}

export function FormModal({
  clientId,
  mode,
  data,
  isOpen,
  onClose,
  onSuccess,
  selectedInfo,
}: Readonly<FormModalProps>): ReactElement | null {
  const [loading, setLoading] = useState(false);
  const [unitOptions, setUnitOptions] = useState<Option[]>([]);
  const [defaultValues, setDefaultValues] = useState<{
    defaultUnit: Option | undefined,
    defaultValues: DefaultFormValues
  } | undefined>();
  const [internalOpen, setInternalOpen] = useState(false);
  const [profile] = useState(getUserProfile);
  const { t } = useTranslation();

  const { modalReference } = useModalControl(isOpen, onClose);

  function onChangeUnit(unitOption: Option): void {
    const currentYear = new Date().getFullYear();

    const goalsUnit = data?.filter((item) => String(item.UNIT_ID) === unitOption.value) ?? [];

    const formValues = goalsUnit.length > 0 ? prepareAllUnitDataToForm(goalsUnit) : defaultFormValues;

    setDefaultValues({
      defaultValues: {
        formValues,
        year: currentYear,
      },
      defaultUnit: undefined,
    });
  }

  function initFormValues(): void {
    if (!selectedInfo || !data) {
      setDefaultValues({
        defaultUnit: undefined,
        defaultValues: {
          formValues: defaultFormValues,
          year: new Date().getFullYear(),
        },
      });

      return;
    }

    const goalsUnit = data.filter((item) => item.UNIT_ID === selectedInfo.unitId);

    const option = unitOptions.find((item) => item.value === selectedInfo.unitId.toString());
    if (goalsUnit.length === 0 || !option) return;

    const formValues = prepareAllUnitDataToForm(goalsUnit);

    setDefaultValues({
      defaultUnit: option,
      defaultValues: {
        year: selectedInfo.year,
        formValues,
      },
    });
  }

  async function getUnitOptions(): Promise<void> {
    setLoading(true);
    const response = await getUnitList(Number(clientId), handleUnitStatusByProfile(profile));
    setUnitOptions(response.map((item) => ({
      label: item.UNIT_NAME,
      value: item.UNIT_ID.toString(),
    })));

    setLoading(false);
  }

  useEffect(() => {
    if (clientId) {
      getUnitOptions();
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      initFormValues();
      setInternalOpen(true);
    } else {
      setInternalOpen(false);
      setDefaultValues(undefined);
    }
  }, [isOpen]);

  if (!isOpen && !internalOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen && internalOpen}>
      <ModalContainer
        ref={modalReference}
        data-testid="energy-goal-form-modal"
      >
        <ModalHeader>
          <div>
            <h3>{t(mode === 'register' ? 'cadastroMetaDeConsumoEnergia' : 'editarMetadeConsumoEnergia')}</h3>
          </div>
          <CloseButton onClick={onClose}>
            <CloseModalX />
          </CloseButton>
        </ModalHeader>
        <ModalContent>
          <FormModalContent
            loading={loading}
            unitOptions={unitOptions}
            onClose={onClose}
            onChangeUnit={onChangeUnit}
            onSuccess={onSuccess}
            defaultUnit={defaultValues?.defaultUnit}
            defaultValues={defaultValues?.defaultValues}
          />
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
}
